import { supportsAudienceReading } from "./manageAccessState";
import type { ToolSelectionTool } from "@/components/tool-selection/ToolSelectionPanel";
import type { ResourceAudienceEntry } from "@gram/client/models/components/resourceaudienceentry.js";
import {
  coversTool,
  isUnnarrowed,
  narrowingLabel,
  type AudienceLevel,
} from "./serverAudience";

/**
 * One row per principal, with a line for each of the three things it can be
 * given on a server. `mcp:connect`, `mcp:read` and `mcp:write` are separate
 * scopes, so "who can use this server" is three answers per principal, not
 * one — and a role reaching the server belongs in the same list as a person,
 * since an administrator reads them together.
 *
 * Every line is resolved the way the server resolves it: gather every rule
 * that reaches this principal at this scope, gather every block that takes it
 * away, and answer from the two sets. Grants add and blocks subtract, so
 * picking one contributing rule — the nearest, the first, the row's own —
 * gives an answer that is right only by luck. What the line reads, what its
 * menu offers, and what a click writes all come from the same resolution.
 *
 * One precedence rule shapes that resolution. A person's own rule naming this
 * server outranks a block reaching them through a role or everyone, as far as
 * that rule reaches; their own blocks always apply. An agent's rules never
 * outrank such a block. Mirrors server/internal/authz/precedence.go.
 */

/** The scopes a row shows, weakest first. */
export type ScopeKey = Extract<AudienceLevel, "use" | "view" | "manage">;

export const SCOPE_ROWS: { key: ScopeKey; label: string; hint: string }[] = [
  { key: "use", label: "Connect", hint: "Call this server's tools" },
  { key: "view", label: "View", hint: "See it in the catalogue" },
  { key: "manage", label: "Manage", hint: "Change its settings and access" },
];

/**
 * The scopes whose grant satisfies a check for this one, itself included.
 * Mirrors scopeExpansions in authz/scopes.go: read and write both satisfy
 * connect, and write satisfies read.
 */
const SATISFIED_BY: Record<ScopeKey, ScopeKey[]> = {
  use: ["use", "view", "manage"],
  view: ["view", "manage"],
  manage: ["manage"],
};

/**
 * The level that takes one scope away. Each block stands on its own — see
 * scopeExpansions in authz/scopes.go, where the mcp:blocked_* scopes do not
 * satisfy one another — so closing connect leaves view and manage as they
 * were, and taking a principal off the server writes all three.
 */
export const BLOCK_LEVEL: Record<ScopeKey, AudienceLevel> = {
  use: "blocked",
  view: "blocked_view",
  manage: "blocked_manage",
};

// Defined next to the reach resolution it is part of, and re-exported here so
// the two surfaces cannot drift when the selector fields change.
export { isUnnarrowed } from "./serverAudience";

/** The scope a block level was written for, or null if it is not a block. */
function blockedScope(level: AudienceLevel): ScopeKey | null {
  return SCOPE_ROWS.find((row) => BLOCK_LEVEL[row.key] === level)?.key ?? null;
}

/** Everything that decides one scope for one principal. */
export interface ScopeCell {
  scope: ScopeKey;
  /** The principal this line belongs to. */
  principalUrn: string;
  /**
   * Every rule granting this scope to this principal: its own, the ones
   * covering every server, and the ones belonging to a role it is in. A
   * stronger scope counts, since it satisfies the same check.
   */
  grants: ResourceAudienceEntry[];
  /** Every block taking this scope away from this principal. */
  blocks: ResourceAudienceEntry[];
  /**
   * This person's own rules naming this server that grant this scope. They
   * outrank blocks reaching the person through a role or everyone. Always
   * empty for anyone but a person.
   */
  direct: ResourceAudienceEntry[];
  /** This row's own rule naming this server, the one this page rewrites. */
  own?: ResourceAudienceEntry;
  /** This row's own block naming this server, the one this page can lift. */
  ownBlock?: ResourceAudienceEntry;
}

export interface AccessRow {
  principalUrn: string;
  kind: ResourceAudienceEntry["kind"];
  displayName: string;
  description?: string;
  cells: Record<ScopeKey, ScopeCell>;
  /** Organization members this principal reaches, when the API says. */
  memberIds: string[];
  /** True when no rule on this row names this server. */
  inheritedOnly: boolean;
  /** A reaching rule cannot be interpreted by this dashboard version. */
  accessUnknown: boolean;
}

/** Whether a rule reaches this principal, directly or through a role. */
function reaches(entry: ResourceAudienceEntry, row: AccessRow): boolean {
  if (entry.principalUrn === row.principalUrn) return true;
  // A rule naming a role reaches the people in it, and the agents assigned to
  // it. It does not reach another role, so only person and agent rows widen
  // this way.
  if (row.kind === "agent") {
    const agentId = row.principalUrn.replace(/^agent:/, "");
    return (entry.agentIds ?? []).includes(agentId);
  }
  if (row.kind !== "user") return false;
  const userId = row.principalUrn.replace(/^user:/, "");
  return (entry.memberIds ?? []).includes(userId);
}

function emptyCell(scope: ScopeKey, principalUrn: string): ScopeCell {
  return { scope, principalUrn, grants: [], blocks: [], direct: [] };
}

/**
 * Whether this row's own rules naming the server outrank blocks that reach it
 * through a role or everyone. Only a person's do. A role or everyone is itself
 * what such a block is written against, and an agent's owner can write its
 * rules without being an administrator.
 */
export function outranksInheritedBlocks(row: AccessRow): boolean {
  return row.kind === "user";
}

/** Whether a block reaches this line through a role or everyone. */
function isInherited(cell: ScopeCell, block: ResourceAudienceEntry): boolean {
  return block.principalUrn !== cell.principalUrn;
}

/**
 * Whether a block takes this tool away. The line's own blocks always do. A
 * block reaching it through a role or everyone does not where the line's own
 * rule naming this server covers the tool.
 */
function blockCovers(
  cell: ScopeCell,
  block: ResourceAudienceEntry,
  tool: ToolSelectionTool,
): boolean {
  if (!coversTool(block, tool)) return false;
  if (!isInherited(cell, block)) return true;
  return !cell.direct.some((grant) => coversTool(grant, tool));
}

/**
 * The blocks still standing once this line's own rules have had their say: a
 * block reaching it through a role or everyone is set aside entirely when the
 * line's own rule opens the whole server.
 */
function standingBlocks(cell: ScopeCell): ResourceAudienceEntry[] {
  const outranked = cell.direct.some(isUnnarrowed);
  return cell.blocks.filter(
    (block) => !(outranked && isInherited(cell, block)),
  );
}

/**
 * Group the rules deciding access to one server into a row per principal. The
 * API returns one entry per principal and level, widest principal first, and
 * that order is what the list keeps.
 */
export function buildAccessRows(entries: ResourceAudienceEntry[]): AccessRow[] {
  const rows = new Map<string, AccessRow>();

  for (const entry of entries) {
    let row = rows.get(entry.principalUrn);
    if (!row) {
      row = {
        principalUrn: entry.principalUrn,
        kind: entry.kind,
        displayName: entry.displayName,
        description: entry.description,
        cells: {
          use: emptyCell("use", entry.principalUrn),
          view: emptyCell("view", entry.principalUrn),
          manage: emptyCell("manage", entry.principalUrn),
        },
        memberIds: [],
        inheritedOnly: true,
        accessUnknown: false,
      };
      rows.set(entry.principalUrn, row);
    }
    if (entry.appliesTo === "resource") row.inheritedOnly = false;
    // Every rule for a principal reports the same membership; the first one
    // that carries it is as good as any.
    if (row.memberIds.length === 0 && entry.memberIds) {
      row.memberIds = entry.memberIds;
    }
  }

  for (const row of rows.values()) {
    for (const entry of entries) {
      if (!reaches(entry, row)) continue;
      if (!supportsAudienceReading([entry])) {
        row.accessUnknown = true;
        continue;
      }
      const own = entry.principalUrn === row.principalUrn;
      const blocked = blockedScope(entry.level);

      if (blocked) {
        // A block reaches the scope it names and nothing else.
        row.cells[blocked].blocks.push(entry);
        if (own && entry.appliesTo === "resource") {
          row.cells[blocked].ownBlock = entry;
        }
        continue;
      }

      const level = entry.level as ScopeKey;
      const direct =
        own && entry.appliesTo === "resource" && outranksInheritedBlocks(row);
      for (const { key } of SCOPE_ROWS) {
        if (!SATISFIED_BY[key].includes(level)) continue;
        row.cells[key].grants.push(entry);
        if (direct) row.cells[key].direct.push(entry);
      }
      if (own && entry.appliesTo === "resource") row.cells[level].own = entry;
    }
  }

  return [...rows.values()];
}

/**
 * The people who keep access to this server after `group` is blocked on it:
 * those it reaches whose own rule naming this server is in force, since it
 * outranks a block written against a role or everyone. A rule their own block
 * already cancels keeps nothing.
 */
/**
 * Whether this line's own rules naming the server still reach something past
 * its own blocks. Blocks from a role or everyone do not count: the rules
 * outrank them. Without a catalogue, only an unnarrowed own block is known to
 * cancel them.
 */
function keepsThroughOwnRules(
  cell: ScopeCell,
  catalog: ToolSelectionTool[],
): boolean {
  if (cell.direct.length === 0) return false;
  const ownBlocks = cell.blocks.filter((block) => !isInherited(cell, block));
  if (catalog.length === 0) return !ownBlocks.some(isUnnarrowed);
  return catalog.some(
    (tool) =>
      cell.direct.some((grant) => coversTool(grant, tool)) &&
      !ownBlocks.some((block) => coversTool(block, tool)),
  );
}

export function keptIndividually(
  group: AccessRow,
  rows: AccessRow[],
  /** The server's tools, so a narrowed rule its own block cancels keeps nothing. */
  catalog: ToolSelectionTool[] = [],
): AccessRow[] {
  return rows.filter((row) => {
    if (row === group || !outranksInheritedBlocks(row)) return false;
    const keeps = SCOPE_ROWS.some(({ key }) =>
      keepsThroughOwnRules(row.cells[key], catalog),
    );
    if (!keeps) return false;
    if (group.kind === "everyone") return true;
    return group.memberIds.includes(row.principalUrn.replace(/^user:/, ""));
  });
}

/**
 * The tools this scope actually reaches: everything its grants open, minus
 * everything its blocks take away. Null when the server publishes no
 * catalogue, since there is then nothing to resolve names against.
 */
export function reachableTools(
  cell: ScopeCell,
  catalog: ToolSelectionTool[],
): string[] | null {
  if (catalog.length === 0 || cell.grants.length === 0) return null;
  return catalog
    .filter((tool) => cell.grants.some((grant) => coversTool(grant, tool)))
    .filter(
      (tool) => !cell.blocks.some((block) => blockCovers(cell, block, tool)),
    )
    .map((tool) => tool.name);
}

/** What a scope line reads, and what it can be changed to. */
export interface ScopeState {
  /** The value shown in the sentence. */
  value: string;
  /** True when a rule grants this line and nothing takes it away. */
  granted: boolean;
  /**
   * True when this row's own block closes the line. A blocking role reads
   * differently from one nothing was granted to: its block outranks grants.
   */
  blocked: boolean;
  /**
   * True when closing or narrowing this line has to be written as a block:
   * something other than this row's own rule on this server is granting it,
   * and grants only ever add.
   */
  subtracts: boolean;
  /** True when the line can be turned off here at all. */
  canRevoke: boolean;
  /** Another principal this line comes from, when one decides it. */
  via?: string;
  /** That principal's URN, so the name can link to it. */
  viaPrincipalUrn?: string;
  /**
   * Where the line comes from when a stronger scope on this server, held by
   * this row's own principal, decides it. Naming the principal there reads as
   * "via itself", which says nothing.
   */
  note?: string;
  /**
   * True when a block this row can neither lift nor outrank caps how far the
   * line reaches. A person's own rule here outranks a block from a role or
   * everyone, so such a block never caps a person's row.
   */
  capped?: boolean;
}

/** Grants on this line other than the rule this page owns for this row. */
function foreignGrants(
  row: AccessRow,
  scope: ScopeKey,
): ResourceAudienceEntry[] {
  const cell = row.cells[scope];
  return cell.grants.filter((grant) => grant !== cell.own);
}

/**
 * The grants other than this row's own rule for the line that still open it.
 * An unnarrowed block from a role or everyone cancels them, except the row's
 * other own rules naming this server, which outrank it.
 */
export function effectiveForeignGrants(
  row: AccessRow,
  scope: ScopeKey,
): ResourceAudienceEntry[] {
  const cell = row.cells[scope];
  const cancelled = cell.blocks.some(
    (block) => isInherited(cell, block) && isUnnarrowed(block),
  );
  return foreignGrants(row, scope).filter(
    (grant) => !cancelled || cell.direct.includes(grant),
  );
}

/**
 * Whether something other than this row's own rule opens the whole line: an
 * unnarrowed grant no block reaching the row through a role or everyone trims.
 * A person's own rules naming this server are never trimmed by such
 * a block.
 */
export function openedElsewhere(row: AccessRow, scope: ScopeKey): boolean {
  const cell = row.cells[scope];
  const inherited = cell.blocks.some((block) => isInherited(cell, block));
  return effectiveForeignGrants(row, scope).some(
    (grant) =>
      isUnnarrowed(grant) && (!inherited || cell.direct.includes(grant)),
  );
}

/** The standing blocks on this line that this page cannot lift from this row. */
function foreignBlocks(
  row: AccessRow,
  cell: ScopeCell,
): ResourceAudienceEntry[] {
  return standingBlocks(cell).filter(
    (block) =>
      block.principalUrn !== row.principalUrn || block.appliesTo !== "resource",
  );
}

/**
 * The foreign blocks this row cannot get past by writing its own rule here: a
 * person outranks blocks from a role or everyone, so only their own
 * rules covering every server remain.
 */
export function cappingBlocks(
  row: AccessRow,
  cell: ScopeCell,
): ResourceAudienceEntry[] {
  const foreign = foreignBlocks(row, cell);
  if (!outranksInheritedBlocks(row)) return foreign;
  return foreign.filter((block) => !isInherited(cell, block));
}

/**
 * Whether nothing on this line is reachable. With a catalogue the answer is
 * exact; without one, only an unnarrowed block the line's own rules do not
 * outrank is known to close it.
 */
function isClosed(cell: ScopeCell, reachable: string[] | null): boolean {
  if (reachable) return reachable.length === 0;
  return standingBlocks(cell).some(
    (block) =>
      isUnnarrowed(block) &&
      (!isInherited(cell, block) || cell.direct.length === 0),
  );
}

/**
 * Whether this line's own blocks alone close it: an unnarrowed one, or, with
 * a catalogue, narrowed ones that between them cover every tool the grants
 * open. A block trimming only some of those tools leaves the line open.
 */
function closedByOwnBlocks(
  cell: ScopeCell,
  catalog: ToolSelectionTool[],
): boolean {
  const own = cell.blocks.filter((block) => !isInherited(cell, block));
  if (own.some(isUnnarrowed)) return true;
  if (own.length === 0 || catalog.length === 0) return false;
  const granted = catalog.filter((tool) =>
    cell.grants.some((grant) => coversTool(grant, tool)),
  );
  return (
    granted.length > 0 &&
    granted.every((tool) => own.some((block) => coversTool(block, tool)))
  );
}

export function scopeState(
  row: AccessRow,
  scope: ScopeKey,
  catalog: ToolSelectionTool[] = [],
): ScopeState {
  if (row.accessUnknown) {
    return {
      value: "Access unknown",
      granted: false,
      blocked: false,
      subtracts: false,
      canRevoke: false,
      capped: false,
    };
  }
  const cell = row.cells[scope];
  const reachable = reachableTools(cell, catalog);
  const closed = isClosed(cell, reachable);

  if (cell.grants.length === 0 || closed) {
    // A block this row cannot lift is why the line is closed, and it is not
    // this row's to reopen.
    const foreign = foreignBlocks(row, cell);
    const cancelling = foreign.find(isUnnarrowed) ?? foreign[0];
    const cancellingSelf = cancelling?.principalUrn === row.principalUrn;
    // The row's own block decides the line, so naming another principal's
    // block beside it would only repeat the answer.
    const blocked = closedByOwnBlocks(cell, catalog);
    return {
      value: blocked ? "Blocked" : "No access",
      granted: false,
      blocked,
      subtracts: false,
      canRevoke: false,
      via: cancellingSelf || blocked ? undefined : cancelling?.displayName,
      viaPrincipalUrn:
        cancellingSelf || blocked ? undefined : cancelling?.principalUrn,
      note: cancellingSelf && !blocked ? sourceNote(cancelling) : undefined,
      // Any block this row can neither lift nor outrank caps it, whether it
      // closed the line outright or trimmed the last tool away.
      capped: cappingBlocks(row, cell).length > 0,
    };
  }

  // Anything granting this line other than the rule this page owns. It is
  // what a revoke has to subtract from, and what the line comes "via".
  const foreign = cell.grants.filter((grant) => grant !== cell.own);
  // Another principal granting the line is what an administrator wants named,
  // so it wins over this row's own wider rule when both reach it.
  const granting =
    foreign.find((grant) => grant.principalUrn !== row.principalUrn) ??
    foreign[0];
  const grantingSelf = granting?.principalUrn === row.principalUrn;

  return {
    value: scope === "use" ? connectLabel(cell, catalog, reachable) : "Allowed",
    granted: true,
    blocked: false,
    subtracts: effectiveForeignGrants(row, scope).length > 0,
    canRevoke: true,
    via: cell.own || grantingSelf ? undefined : granting?.displayName,
    viaPrincipalUrn:
      cell.own || grantingSelf ? undefined : granting?.principalUrn,
    note: cell.own || !grantingSelf ? undefined : sourceNote(granting),
    capped: cappingBlocks(row, cell).length > 0,
  };
}

/**
 * How to say where a rule of this row's own principal comes from, when it is
 * not the rule this page owns for this line. A rule covering every server
 * gets no note: this page answers for one server, and where else the rule
 * reaches is not what an administrator is reading here.
 */
function sourceNote(entry: ResourceAudienceEntry): string | undefined {
  if (entry.appliesTo === "all_resources") return undefined;
  const label = SCOPE_ROWS.find((row) => row.key === entry.level)?.label;
  return label ? `included in ${label}` : undefined;
}

/**
 * How far connect reaches, said as what it opens rather than what it takes
 * away. Nobody reads "5 tools except 4 tools" and pictures the one that is
 * left, so the count is resolved against the catalogue wherever there is one.
 */
function connectLabel(
  cell: ScopeCell,
  catalog: ToolSelectionTool[],
  reachable: string[] | null,
): string {
  if (reachable) {
    if (reachable.length === catalog.length) return "All tools";
    return reachable.length === 1 ? "1 tool" : `${reachable.length} tools`;
  }

  // Without a catalogue there is nothing to count against, so the rules and
  // the blocks trimming them are all there is to say. When a block from a
  // role or everyone closes the server, only this line's own rules reach past
  // it, so they are the whole answer and that block is not a trim.
  const standing = standingBlocks(cell);
  const closedByInherited =
    cell.direct.length > 0 &&
    standing.some((block) => isInherited(cell, block) && isUnnarrowed(block));
  const reaching = closedByInherited ? cell.direct : cell.grants;
  const blocks = closedByInherited
    ? standing.filter((block) => !isInherited(cell, block))
    : standing;
  const base = reaching.some(isUnnarrowed)
    ? "All tools"
    : capitalize(
        narrowingLabel({
          tools: reaching.flatMap((grant) => grant.tools ?? []),
          dispositions: reaching.flatMap((grant) => grant.dispositions ?? []),
        }),
      );
  if (blocks.length === 0) return base;
  return `${base} except ${narrowingLabel({
    tools: blocks.flatMap((block) => block.tools ?? []),
    dispositions: blocks.flatMap((block) => block.dispositions ?? []),
  })}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The row read at a glance, for a collapsed row: what this principal can do
 * on this server, weakest first. Connect carries how far it reaches inside
 * the server; view and manage are the server itself, so their name is the
 * whole answer.
 */
export function accessSummary(
  row: AccessRow,
  catalog: ToolSelectionTool[] = [],
): string {
  if (row.accessUnknown) return "Access unknown";
  const granted = SCOPE_ROWS.filter(
    ({ key }) => scopeState(row, key, catalog).granted,
  );
  const blocked = blockedScopes(row, catalog);
  // Fully blocked reads as one word; a partial block is named after what
  // the row still has, so the exception sits at the end of the line.
  const blockedPart =
    blocked.length === 0
      ? []
      : blocked.length === SCOPE_ROWS.length
        ? ["Blocked"]
        : [`${listLabels(blocked)} blocked`];
  if (granted.length === 0 && blockedPart.length === 0) return "No access";
  return [
    ...granted.map(({ key, label }) =>
      key === "use" ? scopeState(row, key, catalog).value : label,
    ),
    ...blockedPart,
  ].join(" · ");
}

/** The labels of the scopes this row's own block closes, weakest first. */
export function blockedScopes(
  row: AccessRow,
  catalog: ToolSelectionTool[] = [],
): string[] {
  if (row.accessUnknown) return [];
  return SCOPE_ROWS.filter(
    ({ key }) => scopeState(row, key, catalog).blocked,
  ).map(({ label }) => label);
}

function listLabels(labels: string[]): string {
  if (labels.length < 2) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/**
 * Every rule reaching this row other than the ones it owns on this server.
 * Removing the row has to subtract from these rather than delete them.
 */
export function inheritedGrants(row: AccessRow): ResourceAudienceEntry[] {
  const foreign = new Set<ResourceAudienceEntry>();
  for (const { key } of SCOPE_ROWS) {
    for (const grant of row.cells[key].grants) {
      // Removing the row drops every rule it owns here, whatever scope they
      // sit at, so ownership is the test — not whether this particular cell
      // is the one that rule was written for.
      const owned =
        grant.principalUrn === row.principalUrn &&
        grant.appliesTo === "resource";
      if (!owned) foreign.add(grant);
    }
  }
  return [...foreign];
}

/**
 * The tools a block has to name so that only `selection` is left reachable.
 * Narrowing a rule this page does not own is a subtraction, and a subtraction
 * has to name what it takes away, so this needs the server's whole catalogue.
 */
export function complementTools(
  catalog: ToolSelectionTool[],
  selection: { tools: string[]; dispositions: string[] },
): string[] {
  return catalog
    .filter((tool) => !coversTool(selection, tool))
    .map((tool) => tool.name);
}
